import * as Sentry from '@sentry/vue';
import { uxrr } from '@zyno-io/uxrr-client';
import { createApp } from 'vue';

import { isEmbeddedApp } from '@/global.shared';
import { configureSignupApiClients } from '@/openapi-clients/signup';
import { getSpanAttributes } from '@/otel';
import { excludeChunkLoadErrors } from '@/shared/helpers/chunk-load-error';

import router from './signup.routes';
import SignupRoot from './signup.vue';

// Entry for the standalone signup app — a customer-facing surface with no session, shipped as its
// own Vite entry and embedded by the marketing site. See `bundle-isolation.spec.ts` for what it may
// and may not depend on.
//
// Deliberately NOT here, unlike src/main.ts: reflect-metadata, OTel, SSE, the mobile bridge, uxrr,
// the idle timer, the service worker, permission directives, and `configureApiClients()`. A prospect
// has no session for any of it to act on.
//
// Also deliberately absent for now: `setupVf()` and the FontAwesome stylesheet. Between them they
// account for most of the embedded CX app's payload (~499 KB of JS and ~182 KB of CSS), and the
// signup wizard is inputs, toggles and quantity steppers. Add either only when a step genuinely
// needs it, and measure first.

// Mark this runtime as an embedded, customer-facing surface so shared components adapt. Set before
// mount so the first render already sees it. Both entries share one Vite build, so import.meta.env
// can't distinguish them.
isEmbeddedApp.value = true;

configureSignupApiClients();

const app = createApp(SignupRoot);

if (import.meta.env.VITE_APP_SENTRY_DSN) {
    const matches = import.meta.env.VITE_APP_SENTRY_DSN.match(/\/\/(.+)@.+\/(\d+)$/);
    if (matches) {
        Sentry.init({
            app: app,
            dsn: import.meta.env.VITE_APP_SENTRY_DSN,
            environment: import.meta.env.MODE,
            release: `zynosuite-spa@${import.meta.env.MODE}-${import.meta.env.VITE_APP_VERSION}`,
            tunnel: `https://sentry.s24.dev/proxy/${matches[2]}/${matches[1]}`,
            beforeSend: excludeChunkLoadErrors,
            beforeBreadcrumb(breadcrumb) {
                const url = breadcrumb.category === 'fetch' ? breadcrumb.data?.url : null;
                return url?.includes('s24.dev') ? null : breadcrumb;
            }
        });
    }
}

// Signup is a conversion funnel, so unlike the CX forms app it *does* record — seeing exactly where
// a prospect stalls or drops off is the whole point. Settings mirror src/main.ts: inputs unmasked,
// since ZynoSuite owns this funnel end to end. Card details are never captured either way — they
// live in a cross-origin Stripe iframe that rrweb cannot see into.
uxrr.init({
    endpoint: import.meta.env.VITE_APP_UXRR_ENDPOINT,
    appKey: import.meta.env.VITE_APP_UXRR_APP_KEY,
    version: import.meta.env.VITE_APP_VERSION,
    environment: import.meta.env.MODE,
    logging: {
        consolePrefix: 'ZS:signup:'
    },
    tracing: {
        propagateToOrigins: [/localhost/, /zyno.app/, /zynobackup.net/, /^http:\/\/10\./],
        ignoreUrls: [/freshworks\.com/, /\.s24\.dev/, /version\.json/, /status\.zyno\.app/],
        spanAttributes: getSpanAttributes,
        includeRequestHeaders: 'always',
        includeResponseHeaders: 'always',
        includeRequestBody: 'onError'
    },
    recording: {
        privacy: {
            maskInputs: false,
            consoleLogLevel: ['debug', 'info', 'warn', 'error', 'log', 'assert']
        }
    }
});

app.use(router);

// No Pinia, unlike src/main.ts and cx.main.ts: the signup manager is a plain factory with
// provide/inject (see core/manager.ts), deliberately so two wizards can coexist without colliding on
// a store id. Nothing else in the bundle needs a store — add it back if that changes.

app.mount(document.body);
